import { z } from 'zod';

import { parseItemStrict } from 'utils/parser/parseResponse';

const SignedUrlSuccessResponse = z.object({
  signedUrl: z.string(),
  headers: z.record(z.string()),
});
type SignedUrlSuccessResponse = z.infer<typeof SignedUrlSuccessResponse>;
const SignedUrlErrorResponse = z.object({
  error: z.string(),
});
type SignedUrlErrorResponse = z.infer<typeof SignedUrlErrorResponse>;

const SignedUrlsResponse = z
  .union([SignedUrlSuccessResponse, SignedUrlErrorResponse])
  .array();

function isSuccess(
  response: SignedUrlErrorResponse | SignedUrlSuccessResponse,
): response is SignedUrlSuccessResponse {
  return 'signedUrl' in response;
}

export async function calculateSHA256(file: File): Promise<string> {
  // Read file into ArrayBuffer
  const arrayBuffer = await file.arrayBuffer();

  // Use Web Crypto API to hash
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);

  // Convert buffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

export const processSignedUrls = async (
  files: File[],
  signedUrlsBody: Record<string, unknown>,
) => {
  const signedUrls = parseItemStrict(SignedUrlsResponse, signedUrlsBody, false);

  const results = await Promise.all(
    files.map(async (file, index) => {
      const signedUrl = signedUrls[index];
      if (!signedUrl) return false;
      if (isSuccess(signedUrl)) {
        await fetch(signedUrl.signedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type,
            ...signedUrl.headers,
          },
          body: file,
        });
        return true;
      } else {
        return false;
      }
    }),
  );

  return results.some(Boolean);
};
